<script setup lang="ts">
  import { computed } from "vue";
  import { ArrowRight, ArrowUp, Camera, EyeOff, Image, Mic, Play, SendHorizontal, Star, Video, X } from "lucide-vue-next";
  import type { CardPreviewSpec } from "@utils/constants/cardCatalog";

  const props = defineProps<{ preview: CardPreviewSpec }>();

  const options = computed(() => props.preview.options ?? []);
  const isSelected = (index: number) => props.preview.selected?.includes(index) ?? false;
  const rankOf = (index: number) => (props.preview.selected?.indexOf(index) ?? -1) + 1;
  const hasSend = computed(() => ["multiple", "ranked", "matrix", "images", "slider"].includes(props.preview.kind));
  const showsImagesSend = computed(() => props.preview.kind !== "images" || (props.preview.selected?.length ?? 0) > 0);
  const npsValues = Array.from({ length: 11 }, (_, i) => i);
  const uploadIcon = computed(() => ({ "Tap to record a video": Video, "Tap to record": Mic })[props.preview.cta ?? ""] ?? Camera);

  const bubble = "max-w-[88%] self-start rounded-2xl bg-[image:var(--episto-bot-gradient)] px-3.5 py-2.5 text-13 leading-normal text-foreground shadow-md";
  const userBubble = "max-w-[80%] self-end rounded-2xl bg-[image:var(--episto-answer-gradient)] px-3.5 py-2.5 text-13 leading-normal text-white shadow-md";
  const chip = "rounded-full border bg-background px-3 py-1.5 text-13 font-medium text-primary shadow-md";
  const chipSelected = "border-episto-teal bg-episto-teal/15";
</script>

<template>
  <div
    v-if="preview.kind === 'hidden'"
    class="flex flex-col gap-3">
    <p class="flex items-center gap-2 text-xs text-muted-foreground">
      <EyeOff
        class="size-3.5 shrink-0"
        :stroke-width="1.8" />
      Not shown to respondents
    </p>
    <div class="flex flex-col gap-1">
      <span class="heading-label">Example</span>
      <p class="text-13 leading-normal text-foreground-secondary [overflow-wrap:anywhere]">{{ preview.example }}</p>
    </div>
  </div>

  <div
    v-else-if="preview.kind === 'assistant'"
    class="flex w-full flex-col gap-4 font-sans text-foreground">
    <div class="flex items-center justify-between">
      <span class="text-sm font-semibold">AI Assistant</span>
      <span class="rounded-full bg-foreground px-3 py-1 text-xs font-medium text-background">I'm done</span>
    </div>

    <p class="max-w-[80%] self-end rounded-3xl bg-secondary px-4 py-2 text-13 leading-normal">{{ preview.thread?.[0] }}</p>

    <div class="flex flex-col gap-2 text-13 leading-relaxed">
      <p>{{ preview.thread?.[1] }}</p>
      <ul class="flex list-disc flex-col gap-0.5 pl-5">
        <li
          v-for="option in options"
          :key="option">
          {{ option }}
        </li>
      </ul>
      <div class="flex gap-1.5">
        <span
          v-for="source in preview.sources"
          :key="source"
          class="rounded-full bg-secondary px-2.5 py-0.5 text-2xs text-foreground-secondary">
          {{ source }}
        </span>
      </div>
    </div>

    <div class="flex flex-col gap-1.5 pt-2">
      <div class="flex items-center gap-2 rounded-3xl bg-background py-1.5 pl-4 pr-1.5 shadow-sm ring-1 ring-black/10">
        <span class="flex-1 text-13 text-foreground-subtle">Ask anything</span>
        <span class="grid size-7 place-items-center rounded-full bg-foreground text-background">
          <ArrowUp
            class="size-3.5"
            :stroke-width="2.4" />
        </span>
      </div>
      <p class="text-center text-2xs text-foreground-subtle">Episto study · every exchange is recorded</p>
    </div>
  </div>

  <div
    v-else
    class="flex w-full flex-col gap-2.5 font-chat">
    <div
      v-if="preview.question"
      :class="bubble">
      {{ preview.question }}
    </div>

    <div
      v-if="preview.kind === 'typing'"
      class="flex gap-1 self-start rounded-2xl bg-[image:var(--episto-bot-gradient)] px-3.5 py-3 shadow-md">
      <span
        v-for="n in 3"
        :key="n"
        class="size-1.5 rounded-full bg-foreground-subtle" />
    </div>

    <template v-else-if="preview.kind === 'conversation'">
      <div
        v-for="(line, index) in preview.thread"
        :key="line"
        :class="index % 2 === 0 ? userBubble : bubble">
        {{ line }}
      </div>
    </template>

    <div
      v-else-if="preview.kind === 'media'"
      class="self-start">
      <div
        v-if="preview.media === 'audio'"
        class="flex w-56 items-center gap-2.5 rounded-full border bg-background px-3 py-2 shadow-xs">
        <Play
          class="size-3.5 text-primary"
          :stroke-width="2" />
        <span class="h-1 flex-1 rounded-full bg-secondary" />
        <span class="text-2xs text-foreground-subtle">0:30</span>
      </div>
      <div
        v-else-if="preview.media === 'gallery'"
        class="flex gap-2">
        <div
          v-for="n in 3"
          :key="n"
          class="grid h-24 w-20 place-items-center rounded-xl border bg-secondary">
          <Image class="size-5 text-foreground-subtle" />
        </div>
      </div>
      <div
        v-else
        class="grid h-32 w-56 place-items-center rounded-xl border bg-secondary">
        <component
          :is="preview.media === 'video' ? Play : Image"
          class="size-6 text-foreground-subtle" />
      </div>
    </div>

    <p
      v-if="preview.hint && preview.kind !== 'start'"
      class="pl-1 text-xs text-foreground-subtle">
      {{ preview.hint }}
    </p>

    <div
      v-if="preview.kind === 'single' || preview.kind === 'multiple'"
      class="flex flex-wrap justify-center gap-2">
      <span
        v-for="(option, index) in options"
        :key="option"
        :class="[chip, isSelected(index) && chipSelected]">
        {{ option }}
      </span>
    </div>

    <div
      v-else-if="preview.kind === 'ranked'"
      class="flex flex-wrap justify-center gap-2">
      <span
        v-for="(option, index) in options"
        :key="option"
        :class="[chip, isSelected(index) && chipSelected]">
        {{ option }}<template v-if="rankOf(index)">&nbsp;{{ rankOf(index) }}</template>
      </span>
    </div>

    <div
      v-else-if="preview.kind === 'matrix'"
      class="flex flex-col gap-2.5">
      <div
        v-for="(option, index) in options"
        :key="option"
        class="flex flex-col gap-1.5">
        <p class="flex items-baseline gap-1 text-13 font-medium text-primary">
          {{ option }}
          <span class="text-2xs font-normal text-foreground-subtle">({{ index + 1 }}/{{ options.length }})</span>
        </p>
        <div
          v-if="index === 0"
          class="flex gap-1.5">
          <span
            v-for="answer in ['Poor', 'Fair', 'Good', 'Great']"
            :key="answer"
            :class="[chip, 'flex-1 px-1 text-center text-xs', answer === 'Good' && chipSelected]">
            {{ answer }}
          </span>
        </div>
      </div>
    </div>

    <div
      v-else-if="preview.kind === 'images'"
      class="grid grid-cols-2 gap-2">
      <div
        v-for="(option, index) in options"
        :key="option"
        class="grid h-20 place-items-center rounded-lg border bg-secondary"
        :class="isSelected(index) && 'border-episto-teal ring-2 ring-episto-teal/30'">
        <Image class="size-5 text-foreground-subtle" />
      </div>
    </div>

    <div
      v-else-if="preview.kind === 'nps'"
      class="flex justify-between gap-1">
      <span
        v-for="value in npsValues"
        :key="value"
        class="grid size-6.5 place-items-center rounded-full border bg-background text-2xs font-medium text-primary shadow-xs">
        {{ value }}
      </span>
    </div>

    <div
      v-else-if="preview.kind === 'stars'"
      class="flex justify-center gap-2">
      <Star
        v-for="n in 5"
        :key="n"
        class="size-6.5"
        :class="n <= 4 ? 'fill-episto-teal text-episto-teal' : 'text-border-strong'"
        :stroke-width="1.6" />
    </div>

    <div
      v-else-if="preview.kind === 'slider'"
      class="flex flex-col items-center gap-2 px-2">
      <span class="text-lg font-semibold text-primary">45</span>
      <div class="relative h-1.5 w-full rounded-full bg-secondary">
        <div class="absolute inset-y-0 left-0 w-[45%] rounded-full bg-[image:var(--episto-answer-gradient)]" />
        <div
          class="absolute -top-1.5 left-[45%] size-4.5 -translate-x-1/2 rounded-full border-2 border-episto-teal bg-background shadow-sm" />
      </div>
      <div class="flex w-full justify-between text-2xs text-foreground-subtle">
        <span>0</span>
        <span>100</span>
      </div>
    </div>

    <div
      v-else-if="preview.kind === 'start'"
      class="flex flex-col items-center gap-2.5 pt-1">
      <span class="flex items-center gap-2 rounded-full bg-[image:var(--episto-answer-gradient)] px-6 py-2 text-sm font-medium text-white shadow-sm">
        {{ preview.cta }}
        <ArrowRight class="size-4" />
      </span>
      <p class="text-center text-2xs leading-normal text-foreground-subtle">{{ preview.hint }}</p>
    </div>

    <div
      v-else-if="preview.kind === 'upload'"
      class="flex justify-center pt-1">
      <span class="flex items-center gap-2 rounded-full bg-[image:var(--episto-answer-gradient)] px-4 py-2.5 text-13 font-medium text-white shadow-sm">
        <component
          :is="uploadIcon"
          class="size-4" />
        {{ preview.cta }}
      </span>
    </div>

    <div
      v-if="['text', 'conversation'].includes(preview.kind)"
      class="mt-1 flex items-center gap-2 border-t pt-3">
      <span class="flex-1 rounded-lg bg-secondary px-3 py-2 text-13 text-foreground-subtle">
        {{ preview.placeholder ?? "Your message" }}
      </span>
      <SendHorizontal
        class="size-4.5 text-primary"
        :stroke-width="1.8" />
    </div>

    <div
      v-else-if="preview.kind === 'inputs'"
      class="mt-1 flex flex-col gap-2 rounded-xl border bg-background p-3">
      <div
        v-for="n in 3"
        :key="n"
        class="flex items-center gap-2">
        <span class="flex-1 rounded-md bg-secondary px-2.5 py-1.5 text-xs text-foreground-subtle">Answer {{ n }}</span>
        <X class="size-3.5 text-foreground-subtle" />
      </div>
      <SendHorizontal
        class="size-4.5 self-end text-primary"
        :stroke-width="1.8" />
    </div>

    <div
      v-else-if="hasSend && showsImagesSend"
      class="mt-1 flex justify-end border-t pt-3">
      <span class="grid size-8 place-items-center rounded-full bg-background shadow-sm">
        <SendHorizontal
          class="size-4 text-primary"
          :stroke-width="1.8" />
      </span>
    </div>
  </div>
</template>
